// The one place components ask "how do I show this number?" — reads the
// user's unit preferences off the /me cache and returns bound display and
// parse helpers. Storage everywhere stays canonical (kg, cm, kcal, g, ml);
// only rendering and input pass through here.
import { useMe } from "./useMe";
import {
  DEFAULT_UNITS, displayEnergy, displayGlucose, displayLength, displayServing,
  displayVolume, displayWeight, parseLengthInput, parseWeightInput, type UnitPrefs,
} from "@shared/units";

export interface UnitsApi {
  prefs: UnitPrefs;
  /** "82.4 kg" / "181.7 lb" */
  weight: (kg: number) => string;
  weightParts: (kg: number) => { value: number; unit: string };
  parseWeight: (displayValue: number) => number;
  /** "180 cm" / "70.9 in" */
  length: (cm: number) => string;
  parseLength: (displayValue: number) => number;
  /** value+label for calories: 2130 kcal / 8912 kJ */
  energy: (kcal: number) => { value: number; unit: string };
  /** "620 kcal" / "2594 kJ" */
  energyStr: (kcal: number) => string;
  serving: (g: number) => { value: number; unit: string };
  volume: (ml: number) => { value: number; unit: string };
  glucose: (mgdl: number) => { value: number; unit: string };
}

export function useUnits(): UnitsApi {
  const { me } = useMe();
  const prefs: UnitPrefs = me?.unit_prefs ?? DEFAULT_UNITS;
  return {
    prefs,
    weight: (kg) => { const d = displayWeight(kg, prefs); return `${d.value} ${d.unit}`; },
    weightParts: (kg) => displayWeight(kg, prefs),
    parseWeight: (v) => parseWeightInput(v, prefs),
    length: (cm) => { const d = displayLength(cm, prefs); return `${d.value} ${d.unit}`; },
    parseLength: (v) => parseLengthInput(v, prefs),
    energy: (kcal) => displayEnergy(kcal, prefs),
    energyStr: (kcal) => { const d = displayEnergy(kcal, prefs); return `${d.value} ${d.unit}`; },
    serving: (g) => displayServing(g, prefs),
    volume: (ml) => displayVolume(ml, prefs),
    glucose: (mgdl) => displayGlucose(mgdl, prefs),
  };
}
